import classNames from 'classnames';
import type { TableDataValue } from 'components/DataTableTooltip';
import DataTableTooltip from 'components/DataTableTooltip';
import DurationFormat from 'components/DurationFormat';
import { HOUR_MINUTES_POSTFIXED_DATE_FORMAT } from 'constants/datetime';
import { EMPTY_ARRAY } from 'constants/defaults';
import { usePreviousUntilChanged } from 'hooks/usePreviousUntilChanged';
import { range, sum } from 'lodash-es';
import { DateTime } from 'luxon';
import React, { useMemo, useRef, useState } from 'react';
import { getDailyBaseCapacityByDate, parseCapacityHistory } from 'redux/selectors/utils/capacities';
import type { Hour } from 'types/hour';
import type { RawCapacity } from 'types/summaries';
import { getFirstDayOfWeek } from 'utils/date-helper';
import { fromISO } from 'utils/luxon-helpers';
import { groupBy, notNull } from 'utils/typesafe';

import styles from './GanttChart.module.scss';
import type { GanttLayoutMeta, GanttTaskWithLayout } from './gantt-internal-types';
import { USER_CAPACITY_ROW_HEIGHT, USER_CAPACITY_ROW_TOP } from './gantt-layout';
import type { GanttTask } from './gantt-types';

interface Props {
  layoutMeta: GanttLayoutMeta;
  tasks: Array<GanttTaskWithLayout>;
  hiddenTasks: Array<GanttTaskWithLayout>;
  capacities: Array<RawCapacity>;
  scrollLeft: number;
  scrollAreaWidth: number;
  isUnassigned: boolean;
  defaultWeekDays: Array<string>; // MON, TUE, WED, THU, FRI, SAT, SUN
  weekStartsOn: 'monday' | 'sunday';
  zoomView: 'day' | 'week' | 'month';
  hours: Array<Hour<number>>;
}
const GanttChartAvailabilityLayer: React.FC<Props> = ({
  scrollLeft,
  scrollAreaWidth,
  layoutMeta,
  capacities,
  tasks,
  hiddenTasks,
  isUnassigned,
  defaultWeekDays,
  zoomView,
  weekStartsOn,
  hours,
}) => {
  const [TODAY] = useState(DateTime.local().startOf('day'));
  // Pre-parse strings into datetimes for performance reasons (much faster capacity lookups later)
  const parsedCapacities = useMemo(() => parseCapacityHistory(capacities), [capacities]);

  const { firstColumn, numColumns } = useMemo(() => {
    const firstColumn = Math.floor(scrollLeft / layoutMeta.zoom.columnWidth);
    const numColumns = Math.ceil(scrollAreaWidth / layoutMeta.zoom.columnWidth);
    return { firstColumn, numColumns };
  }, [layoutMeta, scrollLeft, scrollAreaWidth]);

  const isWorkday = useMemo(() => {
    const weekdaysSet = new Set(defaultWeekDays);
    const cache: Record<string, boolean> = {};
    return (date: string) =>
      (cache[date] ??= weekdaysSet.has(fromISO(date).weekdayShort.toUpperCase()));
  }, [defaultWeekDays]);

  const getBaseCapacityByDate = useMemo(() => {
    const cache: Record<string, number | null> = {};
    return (date: string) =>
      (cache[date] ??= getDailyBaseCapacityByDate.dateTime(parsedCapacities, date));
  }, [parsedCapacities]);

  const startDate = useMemo(() => fromISO(layoutMeta.startDate), [layoutMeta.startDate]);

  interface AvailabilityBlock {
    key: string;
    startOffset: number;
    width: number;
    dates: Array<string>;
  }
  const structure = useMemo((): Array<AvailabilityBlock> => {
    const blocks: Array<AvailabilityBlock> = [];
    let date = startDate.plus({ days: firstColumn });
    let offset = 0;
    while (offset <= firstColumn + numColumns) {
      const startOfPeriod =
        zoomView === 'week'
          ? getFirstDayOfWeek.core(date, weekStartsOn === 'monday' ? 1 : 0)
          : date.startOf(zoomView);
      const endDate = startOfPeriod.plus({ [zoomView]: 1 });
      const endOffset = Math.ceil(endDate.diff(startDate, 'days').days);
      const width = endOffset - offset;
      if (width > 0 && endDate >= TODAY && date >= TODAY) {
        blocks.push({
          key: date.toISODate(),
          startOffset: offset,
          width,
          dates: range(width).map(i => date.plus({ days: i }).toISODate()),
        });
      }
      offset = endOffset;
      date = endDate;
    }
    return blocks.length > 0 ? blocks : EMPTY_ARRAY;
  }, [TODAY, firstColumn, numColumns, startDate, weekStartsOn, zoomView]);

  const stableTasks = usePreviousUntilChanged(tasks);
  const stableHiddenTasks = usePreviousUntilChanged(hiddenTasks);
  const dateTimeCache = useRef(new WeakMap<GanttTask, Array<string>>());

  const availabilityMap = useMemo(() => {
    const availabilityMap = structure.reduce<Record<string, AvailabilityForDate>>(
      (acc, { dates }) => {
        dates.forEach(date => {
          const capacity = getBaseCapacityByDate(date);
          acc[date] = {
            type: 'untouched',
            remainingCapacity: capacity,
            baseCapacity: capacity,
            tasks: [],
            hiddenInfluence: false,
          };
        });
        return acc;
      },
      {}
    );
    const relevantTasks = stableTasks
      .concat(stableHiddenTasks)
      .filter(task => !task.task.completed);
    relevantTasks.forEach(task => {
      let taskDays = dateTimeCache.current.get(task.task);
      if (!taskDays) {
        const taskStartDate = fromISO(task.task.startDate);
        const taskEndDate = fromISO(task.task.endDate);
        const startOffset = taskStartDate.diff(startDate, 'days').days;
        const endOffset = taskEndDate.diff(startDate, 'days').days;
        taskDays = range(startOffset, endOffset + 1).map(offset =>
          startDate.plus({ days: offset }).toISODate()
        );
        dateTimeCache.current.set(task.task, taskDays);
      }

      const daysToDistribute = sum(
        taskDays.map(date => {
          if (isUnassigned) {
            return isWorkday(date) ? 1 : 0;
          } else {
            return getBaseCapacityByDate(date) ? 1 : 0;
          }
        })
      );
      taskDays.forEach(date => {
        const baseCapacity = getBaseCapacityByDate(date);
        if (availabilityMap[date]) {
          availabilityMap[date].type = 'touched';
        }
        if ((baseCapacity != null && baseCapacity > 0) || (isUnassigned && isWorkday(date))) {
          if (availabilityMap[date] == null) {
            availabilityMap[date] = {
              type: 'touched',
              remainingCapacity: 0,
              baseCapacity,
              tasks: [],
              hiddenInfluence: false,
            };
          }
          const subtractedCapacity = task.task.estimated_minutes_per_user / 60 / daysToDistribute;
          availabilityMap[date]!.remainingCapacity! -= subtractedCapacity;
          availabilityMap[date]!.tasks!.push({ task: task.task, subtractedCapacity });
          availabilityMap[date]!.hiddenInfluence ||= task.task.hiddenByFilter;
        }
      });
    });

    // Subtract planned hours from remaining capacity
    for (const day in availabilityMap) {
      const currentAvailabilityMap = availabilityMap[day];
      currentAvailabilityMap.totalPlanned = 0;
      hours
        .filter(h => h.day === day)
        .forEach(matchingHour => {
          if (matchingHour.forecast_id && matchingHour.estimated_duration.total_minutes > 0) {
            const taskItem = relevantTasks.find(item => item.task.id === matchingHour.forecast_id);
            if (taskItem && taskItem.task.startDate <= day && day <= taskItem.task.endDate) {
              // Planned time against any of the user's tasks for that day should not deduct from capacity
              return;
            }
          }
          currentAvailabilityMap.totalPlanned! += matchingHour.estimated_duration.total_hours;
        });
      currentAvailabilityMap.remainingCapacity! -= currentAvailabilityMap.totalPlanned;
    }

    return availabilityMap;
  }, [
    structure,
    stableTasks,
    stableHiddenTasks,
    getBaseCapacityByDate,
    startDate,
    isUnassigned,
    isWorkday,
    hours,
  ]);

  const cells = useMemo(
    () =>
      structure
        .map(({ key, dates, startOffset, width: widthInColumns }) => {
          const availabilities = dates.map(date => availabilityMap[date] ?? null);
          if (
            availabilities.every(
              availability => availability == null || availability.type === 'untouched'
            )
          ) {
            return null;
          }
          const left = startOffset * layoutMeta.zoom.columnWidth - layoutMeta.zoom.dayPadding / 2;
          const width = layoutMeta.zoom.columnWidth * widthInColumns;
          const { remainingCapacity, baseCapacity } = sumAvailabilities(availabilities);
          if (
            (remainingCapacity == null || baseCapacity == null) &&
            // Fallback check for unassigned - check via default workdays
            !(isUnassigned && dates.some(date => isWorkday(date)))
          ) {
            return {
              key,
              width,
              left,
              type: 'none',
              label: '-',
            };
          }

          const roundedCapacity = Math.round((remainingCapacity ?? 0) * 4) / 4;
          return {
            key,
            left,
            width,
            type: getCapacityClass(roundedCapacity),
            label: isUnassigned ? Math.abs(roundedCapacity) : roundedCapacity,
            tooltip: () => {
              const tasks = combineTaskInfluences(
                availabilities.flatMap(availability => availability?.tasks)
              );
              const tooltipValues: Array<TableDataValue> = [];
              if (!isUnassigned) {
                tooltipValues.push(
                  {
                    label: getCapacityLabel(zoomView),
                    value: (
                      <span>
                        <DurationFormat
                          value={baseCapacity}
                          format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
                          valueType="hours"
                        />
                      </span>
                    ),
                    align: 'right',
                    bulletVariant: 'muted',
                  },
                  'divider'
                );
              }

              let remainingCapacity = baseCapacity ?? 0;
              tasks.forEach(({ task, subtractedCapacity }) => {
                remainingCapacity -= subtractedCapacity;

                tooltipValues.push({
                  label: [task.title, task.hiddenByFilter ? '(Filtered)' : null]
                    .filter(notNull)
                    .join(' '),
                  value: (
                    <span>
                      <DurationFormat
                        value={isUnassigned ? Math.abs(subtractedCapacity) : -subtractedCapacity}
                        format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
                        valueType="hours"
                      />
                    </span>
                  ),
                  align: 'right',
                });
              });

              // Adding planned hours
              tooltipValues.push('divider');
              const totals = {
                planned: 0,
              };
              dates.forEach(date => {
                if (availabilityMap[date]) {
                  totals.planned += availabilityMap[date].totalPlanned ?? 0;
                }
              });
              remainingCapacity -= totals.planned;
              tooltipValues.push({
                label: 'Total planned',
                value: (
                  <span>
                    <DurationFormat
                      value={-totals.planned}
                      format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
                      valueType="hours"
                    />
                  </span>
                ),
                align: 'right',
              });

              tooltipValues.push('divider');
              let availableCapacityStyle = {};
              if (!isUnassigned) {
                availableCapacityStyle = {
                  color: `var(--reference_palette_${
                    remainingCapacity > 0 ? 'eucalyptus_30' : 'red_30'
                  })`,
                };
              }
              tooltipValues.push({
                label: (
                  <span style={availableCapacityStyle}>
                    {isUnassigned ? 'Unassigned tasks' : 'Available capacity'}
                  </span>
                ),
                value: (
                  <span style={availableCapacityStyle}>
                    <DurationFormat
                      value={isUnassigned ? Math.abs(remainingCapacity) : remainingCapacity}
                      format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
                      valueType="hours"
                    />
                  </span>
                ),
                align: 'right',
                bulletVariant: isUnassigned
                  ? 'neutral'
                  : roundedCapacity > 0
                    ? 'success'
                    : roundedCapacity < 0
                      ? 'alert'
                      : 'neutral',
              });

              return tooltipValues;
            },
          };
        })
        .filter(notNull),
    [
      structure,
      layoutMeta.zoom.columnWidth,
      layoutMeta.zoom.dayPadding,
      isUnassigned,
      availabilityMap,
      isWorkday,
      zoomView,
    ]
  );

  return (
    <div
      className={styles.availabilityLayer}
      style={{
        height: USER_CAPACITY_ROW_HEIGHT,
        top: USER_CAPACITY_ROW_TOP,
      }}
    >
      {cells.map(cell => {
        const label = <div className={styles.availabilityLabel}>{cell.label}</div>;
        return (
          <div
            key={cell.key}
            className={classNames(styles.availabilityCell, styles[cell.type], styles[zoomView], {
              [styles.hoverable]: cell.tooltip != null,
              [styles.unassigned]: isUnassigned,
            })}
            style={{ width: cell.width, left: cell.left }}
          >
            {cell.tooltip != null ? (
              <DataTableTooltip position="top" dataValues={cell.tooltip}>
                {label}
              </DataTableTooltip>
            ) : (
              label
            )}
          </div>
        );
      })}
    </div>
  );
};
export default GanttChartAvailabilityLayer;

type CapacityClass = 'spare-capacity' | 'over-capacity' | 'full-capacity';
function getCapacityClass(capacity: number): CapacityClass {
  return capacity > 0 ? 'spare-capacity' : capacity < 0 ? 'over-capacity' : 'full-capacity';
}

function getCapacityLabel(zoomView: 'day' | 'week' | 'month') {
  if (zoomView === 'day') {
    return 'Daily capacity';
  }
  if (zoomView === 'week') {
    return 'Weekly capacity';
  }
  return 'Monthly capacity';
}

interface AvailabilityForDate {
  type: string;
  remainingCapacity: number | null;
  baseCapacity: number | null;
  tasks: Array<{ task: GanttTask; subtractedCapacity: number }>;
  hiddenInfluence: boolean;
  totalPlanned?: number;
}
function sumAvailabilities(availabilities: Array<AvailabilityForDate>): {
  remainingCapacity: number | null;
  baseCapacity: number | null;
} {
  return availabilities.reduce(
    (acc, availability) => {
      if (availability.remainingCapacity != null) {
        acc.remainingCapacity ??= 0;
        acc.remainingCapacity += availability.remainingCapacity;
      }
      if (availability.baseCapacity != null) {
        acc.baseCapacity ??= 0;
        acc.baseCapacity += availability.baseCapacity;
      }
      return acc;
    },
    { remainingCapacity: null, baseCapacity: null } as ReturnType<typeof sumAvailabilities>
  );
}
function combineTaskInfluences(
  tasks: ReadonlyArray<{ task: GanttTask; subtractedCapacity: number }>
): ReadonlyArray<{ task: GanttTask; subtractedCapacity: number }> {
  return Object.entries(groupBy(tasks, task => task.task.id)).map(([_, groupedTasks]) => {
    const subtractedCapacity = sum(groupedTasks.map(task => task.subtractedCapacity));
    return {
      task: groupedTasks[0].task,
      subtractedCapacity,
    };
  });
}
